import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select } from "@sagot/ui";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import type { ActionHttpMethod } from "../shared/types";
import { AUTH_TYPES, HTTP_METHODS, MAPPING_LIST_MAX, TIMEOUT_MAX_MS, TIMEOUT_MIN_MS, type ActionFormValues } from "./schema-model";

const PLACEHOLDER_EXAMPLE = "{{param}}";
const WILDCARD_EXAMPLE = "$.items[*].name";

function Headers({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const rows = useFieldArray({ control: form.control, name: "headers" });
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="label mb-0">{t("build.actions.editor.headers")}</span>
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { rows.append({ name: "", value: "" }); }}>
          <Icon name="plus" />
          {t("build.actions.editor.addHeader")}
        </Button>
      </div>
      <div className="space-y-2">
        {rows.fields.map((field, index) => (
          <div key={field.id} className="flex gap-2">
            <Input aria-label={`${t("build.actions.editor.headerName")} ${String(index + 1)}`} className="h-9 font-mono text-xs" disabled={disabled} {...form.register(`headers.${index}.name`)} />
            <Input aria-label={`${t("build.actions.editor.headerValue")} ${String(index + 1)}`} className="h-9 font-mono text-xs" disabled={disabled} {...form.register(`headers.${index}.value`)} />
            <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t("build.actions.editor.headerRemove", { name: field.name || String(index + 1) })} onClick={() => { rows.remove(index); }}>
              <Icon name="trash" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Auth({ disabled, hasSecret }: { disabled: boolean; hasSecret: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const type = useWatch({ control: form.control, name: "auth_type" });
  const errors = form.formState.errors;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label={t("build.actions.editor.auth")}>
        <Select disabled={disabled} {...form.register("auth_type")}>
          {AUTH_TYPES.map((value) => <option key={value} value={value}>{t(`build.actions.editor.authTypes.${value}`)}</option>)}
        </Select>
      </Field>
      {type === "none" ? null : (
        <Field label={t("build.actions.editor.secret")} hint={hasSecret ? t("build.actions.editor.secretSaved") : t("build.actions.editor.secretHint")} error={errors.secret?.message}>
          <Input type="password" autoComplete="new-password" className="font-mono text-xs" disabled={disabled} {...form.register("secret")} />
        </Field>
      )}
      {type === "api_key" ? (
        <Field label={t("build.actions.editor.authHeader")}>
          <Input className="font-mono text-xs" placeholder="X-API-Key" disabled={disabled} {...form.register("auth_header")} />
        </Field>
      ) : null}
    </div>
  );
}

function Mapping({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const rows = useFieldArray({ control: form.control, name: "mapping" });
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="label mb-0">{t("build.actions.editor.mapping")}</span>
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { rows.append({ field: "", path: "$." }); }}>
          <Icon name="plus" />
          {t("build.actions.editor.addMapping")}
        </Button>
      </div>
      <div className="space-y-2">
        {rows.fields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <Input aria-label={`${t("build.actions.editor.mappingField")} ${String(index + 1)}`} placeholder={t("build.actions.editor.mappingFieldPlaceholder")} className="h-9 font-mono text-xs" disabled={disabled} {...form.register(`mapping.${index}.field`)} />
            <Icon name="arrow-left" className="size-4 shrink-0 text-fg-subtle" />
            <Input aria-label={`${t("build.actions.editor.mappingPath")} ${String(index + 1)}`} placeholder={t("build.actions.editor.mappingPathPlaceholder")} className="h-9 font-mono text-xs" disabled={disabled} {...form.register(`mapping.${index}.path`)} />
            <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t("build.actions.editor.mappingRemove", { name: field.field || String(index + 1) })} onClick={() => { rows.remove(index); }}>
              <Icon name="trash" />
            </Button>
          </div>
        ))}
      </div>
      <p className="hint">{t("build.actions.editor.mappingHint")}</p>
      <p className="hint" data-testid="mapping-wildcard-hint">{t("build.actions.editor.mappingListHint", { example: WILDCARD_EXAMPLE, max: MAPPING_LIST_MAX })}</p>
      {form.formState.errors.mapping?.message ? <p className="mt-1 text-xs font-semibold text-danger" role="alert">{form.formState.errors.mapping.message}</p> : null}
    </div>
  );
}

/** Custom HTTP action: request, headers, auth (write-only secret), timeout, response mapping. */
export function HttpEditor({ disabled, hasSecret, onMethodChange }: { disabled: boolean; hasSecret: boolean; onMethodChange?: (method: ActionHttpMethod) => void }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const errors = form.formState.errors;
  const method = form.register("method", {
    onChange: (event: { target: { value: string } }) => { onMethodChange?.(event.target.value as ActionHttpMethod); },
  });
  return (
    <section className="space-y-4 rounded-lg border border-line p-4" aria-labelledby="action-http-title">
      <h4 id="action-http-title" className="flex items-center gap-2 text-sm font-bold">
        <Icon name="webhook" className="size-4 text-primary-ink" />
        {t("build.actions.editor.http")}
      </h4>
      <div className="flex gap-2">
        <Select aria-label={t("build.actions.editor.method")} className="w-28 font-mono text-xs" disabled={disabled} {...method}>
          {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </Select>
        <div className="min-w-0 flex-1">
          <Field label={<span className="sr-only">{t("build.actions.editor.url")}</span>} hint={t("build.actions.editor.urlHint", { example: PLACEHOLDER_EXAMPLE })} error={errors.url?.message}>
            <Input className="font-mono text-xs" placeholder="https://api.example.com/items/{{id}}" disabled={disabled} {...form.register("url")} />
          </Field>
        </div>
      </div>
      <Headers disabled={disabled} />
      <Auth disabled={disabled} hasSecret={hasSecret} />
      <Field label={t("build.actions.editor.timeout")} hint={t("build.actions.editor.timeoutHint")} error={errors.timeout_ms?.message}>
        <Input type="number" min={TIMEOUT_MIN_MS} max={TIMEOUT_MAX_MS} step={500} className="w-40" disabled={disabled} {...form.register("timeout_ms", { valueAsNumber: true })} />
      </Field>
      <Mapping disabled={disabled} />
    </section>
  );
}
